import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Box, Typography, Tabs, Tab, Button } from "@mui/material";
import { buildXlsx } from "../utils/xlsx";
import { FMT, NUMERIC } from "../utils/stockDashboardPayload";
import {
  cardSx, titleSx, countSx, vintageSx, tabsSx, exportButtonSx, scrollSx,
  sortRows, nextSort, headerSortProps, BRAND_L, BRAND_D,
} from "./liveGridStyles";

/**
 * Stock Summary Dashboard — the migrated Power BI grid, rendered natively.
 *
 * Filters come from the page-level bar (see StockFilterBar) so this grid and the
 * model-ranking grid respond to one selection.
 *
 * Values arrive pre-quantised from the server and are only FORMATTED here; see
 * utils/stockDashboardPayload.js for why rounding them would silently break the
 * Power BI parity check.
 */

/* 6,610 detail rows x 20 columns is 130k cells; React reconciles that far more
   slowly than the report's innerHTML string build did, so rows are revealed in
   chunks as you scroll. */
const CHUNK = 200;

/* Notes arrives as a "; "-joined tag list. One chip per tag; the string itself is
   untouched, so the Excel export and the Power BI comparison still see exactly
   what BigQuery returned. */
function NotesCell({ value }) {
  if (!value) return null;
  const tags = String(value).split(";").map((t) => t.trim()).filter(Boolean);
  return (
    <>
      {tags.map((t, i) => {
        const peg = /PEG/i.test(t);
        return (
          <Box
            component="span"
            key={`${i}-${t}`}
            sx={{
              display: "inline-block", px: 0.75, mr: 0.5, borderRadius: 999,
              fontSize: ".7rem", fontWeight: 600, whiteSpace: "nowrap",
              bgcolor: peg ? BRAND_L : "#E8F0FE",
              color: peg ? BRAND_D : "#2a5db0",
            }}
          >
            {t}
          </Box>
        );
      })}
    </>
  );
}

export default function StockSummaryGrid({ data, industry, stock, vintage }) {
  const [view, setView] = useState("summary");
  const [sort, setSort] = useState({});          // per view, so each keeps its own
  const [shown, setShown] = useState(CHUNK);
  const scrollRef = useRef(null);

  const def = data[view];

  const rows = useMemo(() => {
    const kept = def.rows.filter(
      (r) => (!industry || r[def.iIdx] === industry) && (!stock || r[def.sIdx] === stock)
    );
    return sortRows(kept, (i) => NUMERIC.has(def.cols[i][1]), sort[view]);
  }, [def, industry, stock, sort, view]);

  useEffect(() => { setShown(CHUNK); }, [view, industry, stock, sort]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 400) {
      setShown((n) => (n >= rows.length ? n : n + CHUNK));
    }
  }, [rows.length]);

  /** Export exactly what's on screen: current view, current filters, current sort. */
  const exportXlsx = () => {
    const name = view === "summary" ? "Summary" : "Detail";
    const bytes = buildXlsx(def.cols, rows, name);
    const url = URL.createObjectURL(new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }));
    const a = document.createElement("a");
    a.href = url;
    // Same filename the HTML report produced, so saved files stay consistent.
    a.download = `stock_summary_${name.toLowerCase()}_${data.isoDate || "current"}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const key = sort[view];
  const visible = rows.slice(0, shown);

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ ...titleSx, flex: "1 1 240px" }}>Stock Summary Dashboard</Typography>
        <Button variant="contained" size="small" onClick={exportXlsx}
          disabled={!rows.length} sx={exportButtonSx}>
          Export to Excel
        </Button>
      </Box>

      <Tabs value={view} onChange={(_, v) => setView(v)} sx={tabsSx}>
        <Tab value="summary" label="Summary" />
        <Tab value="detail" label="Detail" />
      </Tabs>

      {vintage && <Typography sx={vintageSx}>Data as of {vintage}</Typography>}

      <Box ref={scrollRef} onScroll={onScroll} sx={scrollSx(460)}>
        <table>
          <thead>
            <tr>
              {def.show.map((i) => {
                const [label, fmt] = def.cols[i];
                const { arrow, ...aria } = headerSortProps(key, i);
                return (
                  <th
                    key={label}
                    className={NUMERIC.has(fmt) ? "n" : undefined}
                    onClick={() => setSort((p) => ({ ...p, [view]: nextSort(p[view], i) }))}
                    {...aria}
                  >
                    {label}
                    <span className="arw">{arrow}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!rows.length && (
              <tr>
                <td className="empty" colSpan={def.show.length}>
                  No rows match the current selection.
                </td>
              </tr>
            )}
            {visible.map((r, ri) => (
              <tr key={ri}>
                {def.show.map((i) => {
                  const fmt = def.cols[i][1];
                  return (
                    <td key={i} className={NUMERIC.has(fmt) ? "n" : undefined}>
                      {i === def.nIdx ? <NotesCell value={r[i]} /> : FMT[fmt](r[i])}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      <Typography sx={countSx}>
        {rows.length.toLocaleString("en-US")} of {def.rows.length.toLocaleString("en-US")} rows
        {shown < rows.length && ` · showing ${shown.toLocaleString("en-US")}, scroll for more`}
      </Typography>
    </Box>
  );
}
